import { useCallback, useEffect, useState } from 'react';

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

import constants from '../theme/exportedVariables.module.scss';
import { useDarkMode } from './useDarkMode';

const isCapacitor = Capacitor.isNativePlatform();
const isBackgroundColorSupported = Capacitor.getPlatform() === 'android';

export function useStatusBar() {
	const { palette, isDarkModeOn } = useDarkMode();
	const [currentMode, setCurrentMode] = useState(palette);

	useEffect(() => {
		if (currentMode !== palette) {
			setCurrentMode(palette);
			if (isCapacitor) {
				StatusBar.setStyle({
					style: isDarkModeOn ? Style.Dark : Style.Light,
				});
			}
		}
	}, [palette, setCurrentMode, currentMode, isDarkModeOn]);

	const enableVideoMode = useCallback(() => {
		if (isCapacitor) {
			if (isBackgroundColorSupported) {
				StatusBar.setBackgroundColor({ color: constants.colorDark0 });
			}
			StatusBar.setStyle({ style: Style.Dark });
		}
	}, []);

	const disableVideoMode = useCallback(() => {
		if (isCapacitor) {
			StatusBar.setStyle({ style: Style.Light });

			if (isBackgroundColorSupported) {
				StatusBar.setBackgroundColor({ color: constants.colorLight0 });
			}
		}
	}, []);

	const setStatusBarColor = useCallback(
		(lightColor: string, darkColor: string) => {
			if (isCapacitor && isBackgroundColorSupported) {
				StatusBar.setBackgroundColor({
					color: !isDarkModeOn ? lightColor : darkColor,
				});
			}
		},
		[isDarkModeOn],
	);

	return {
		currentMode,
		setStatusBarColor,
		enableVideoMode,
		disableVideoMode,
	};
}
